import {
  DynamicFieldProperty_Enum,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { useTranslation } from '@frontend/i18n';
import {
  TEMPLATE_TYPE_LINK_MAP,
  useGetDefaultTemplate,
  IntegratedMessaingTrackingPrefixes,
} from '@frontend/integrated-messaging';
import { MessagingSelectInvoiceModal, usePaymentsModalButton } from '@frontend/payments-collection-flow';
import { PaymentsFeatureFlags } from '@frontend/payments-hooks';
import { useAppFlagStore } from '@frontend/shared';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { ComponentProps } from '@frontend/types';
import { UseTemplateFlowArgs, UseTemplateFlowResult } from '../types';

export const useTextToPayTemplateFlow = ({
  groupId,
  personId,
  renderTemplate,
  onOpenSettings,
}: UseTemplateFlowArgs): UseTemplateFlowResult<ComponentProps<typeof MessagingSelectInvoiceModal>> => {
  const { t } = useTranslation('inbox-templates');
  const { paymentRequestModalProps, openModal, closeModal, disabled } = usePaymentsModalButton({ personId });
  const getDefaultTemplate = useGetDefaultTemplate({ groupIds: [groupId] });
  const { featureFlags } = useAppFlagStore();
  const hasFF = !!featureFlags[groupId]?.get(PaymentsFeatureFlags.PaymentLinkMessaging)?.value;
  const showButton = hasFF && !!personId && !disabled;

  return {
    popoverItem: showButton
      ? {
          iconName: 'dollar-sign',
          label: t('Payment Reminder'),
          trackingId: `${InboxPrefixes.Thread}-payment-reminder-button`,
          onClick: () => openModal(),
        }
      : undefined,
    modalProps: {
      ...paymentRequestModalProps,
      invoiceSelectionTrackingPrefix: IntegratedMessaingTrackingPrefixes.InvoiceSelector,
      groupId,
      // We're not returning the button that triggers this modal if there's no personId
      personId: personId ?? '',
      onInsert: ({ invoiceId, textToPayLink, template }) => {
        const resolvedTemplate = template ?? getDefaultTemplate({ type: TemplateType_Slug.MANUAL_PAYMENT_REQUEST });
        if (resolvedTemplate) {
          const relatedType = Object.keys(TEMPLATE_TYPE_LINK_MAP).includes(resolvedTemplate.templateTypeSlug)
            ? TEMPLATE_TYPE_LINK_MAP[resolvedTemplate.templateTypeSlug as keyof typeof TEMPLATE_TYPE_LINK_MAP]
                ?.relatedType
            : undefined;
          renderTemplate({
            template: resolvedTemplate,
            linkData: [{ link: textToPayLink, relatedId: invoiceId, relatedType }],
            additionalBindings: {
              [DynamicFieldProperty_Enum.TEXT_TO_PAY_LINK]: textToPayLink,
            },
          });
          closeModal();
        }
      },
      onOpenSettings,
    },
  };
};
